// src/components/SEO.jsx - Enhanced SEO component
import React from 'react';
import { Helmet } from 'react-helmet-async';

function SEO({
                 title = 'PostMgr - AI-Powered Social Media Management',
                 description = 'Generate engaging social media posts with AI. Schedule across LinkedIn, Instagram, Bluesky, and Twitter. Start free.',
                 image = 'https://postmgr.com/images/og-image.png',
                 url = 'https://postmgr.com',
                 type = 'website',
                 author = 'PostMgr Team',
                 keywords = 'social media management, AI content generation, social media automation, LinkedIn automation, Instagram scheduler, Twitter automation',
                 canonicalUrl,
                 noIndex = false,
                 structuredData
             }) {

    const siteUrl = 'https://postmgr.com';
    const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;
    const canonical = canonicalUrl || fullUrl;

    // Default structured data
    const defaultStructuredData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "PostMgr",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI-powered social media management platform that helps businesses create, schedule, and publish engaging content across multiple social media platforms.",
        "url": siteUrl,
        "author": {
            "@type": "Organization",
            "name": "PostMgr",
            "url": siteUrl
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free plan available"
        },
        "featureList": [
            "AI content generation",
            "Multi-platform publishing",
            "Social media scheduling",
            "Analytics and insights",
            "Team collaboration"
        ]
    };

    const finalStructuredData = structuredData || defaultStructuredData;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={author} />
            <link rel="canonical" href={canonical} />

            {/* Robots */}
            {noIndex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:site_name" content="PostMgr" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content="@postmgr" />
            <meta name="twitter:creator" content="@postmgr" />

            {/* Additional SEO */}
            <meta name="theme-color" content="#2563eb" />
            <meta name="msapplication-TileColor" content="#2563eb" />

            {/* Language and Region */}
            <meta name="language" content="English" />
            <meta name="geo.region" content="US" />
            <meta name="geo.country" content="United States" />

            {/* Mobile Optimization */}
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />

            {/* Favicons */}
            <link rel="icon" type="image/x-icon" href="/favicon.ico" />
            <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
            <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(finalStructuredData)}
            </script>

            {/* Preconnect to external domains */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="preconnect" href="https://api.stripe.com" />

            {/* DNS Prefetch */}
            <link rel="dns-prefetch" href="//fonts.googleapis.com" />
            <link rel="dns-prefetch" href="//fonts.gstatic.com" />
            <link rel="dns-prefetch" href="//api.stripe.com" />
        </Helmet>
    );
}

export default SEO;